/**
 * PersonCard.jsx
 *
 * Renders a single person card in the lineage view — name, dates, and
 * optional small spouse tags underneath.
 *
 * suppressSpouseIds exists because in a couple row the two partners are
 * already shown as full-size cards next to each other. Without suppression,
 * they'd also appear as small spouse tags on each other's cards, which
 * would be redundant.
 *
 * Parent: LineageColumn.jsx (ancestor cards) / LineageView.jsx (target card)
 */

import React from "react";
import { formatPersonName } from "./useAncestryTree";
import { pressOnEnterOrSpace } from "../../shared/keyboard";

function PersonCard({ node, isHighlighted, isTarget, onMouseEnter, onMouseLeave, onClick, suppressSpouseIds }) {
  const { data } = node;
  const name = formatPersonName(data);
  const birth = data?.birthday || "";
  const death = data?.death || "";
  const dateStr = birth || death ? `${birth}${birth && death ? " – " : ""}${death}` : "";

  // Filter out spouses that are already shown as full cards in the couple row
  const suppressSet = suppressSpouseIds ? new Set(suppressSpouseIds.map(String)) : null;
  const visibleSpouses = node.spouses
    ? node.spouses.filter((sp) => !suppressSet || !suppressSet.has(String(sp.id)))
    : [];

  return (
    // When the card can be clicked it can also be reached with Tab and pressed
    // with Enter or Space; keyboard focus lights the path as hovering does.
    <div
      className="lineage-card-wrapper"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      style={{ cursor: onClick ? "pointer" : "default" }}
      {...(onClick
        ? {
            role: "button",
            tabIndex: 0,
            onKeyDown: pressOnEnterOrSpace(onClick),
            onFocus: onMouseEnter,
            onBlur: onMouseLeave,
          }
        : {})}
    >
      <div
        className={`lineage-card${isHighlighted ? " lineage-highlighted" : ""}${isTarget ? " lineage-target" : ""}`}
      >
        <div className="lineage-card-name">{name}</div>
        {dateStr && <div className="lineage-card-dates">{dateStr}</div>}
        {node.duplicate && (
          <div className="lineage-card-note">(also appears above)</div>
        )}
      </div>
      {/* Spouse tags */}
      {visibleSpouses.length > 0 && (
        <div className="lineage-spouse-area">
          {visibleSpouses.map((sp) => {
            const spName = `${sp.data?.first_name || ""} ${sp.data?.last_name || ""}`.trim();
            return (
              <div key={sp.id} className="lineage-spouse-connector">
                <div className="lineage-spouse-line" />
                <div className="lineage-spouse-tag">
                  {spName || "Unknown"}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default React.memo(PersonCard);
